// File: src/components/view/PackageSingleView.js

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MobileNavigation from '../BottomNavigation';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import ArrowCircleLeft from '@mui/icons-material/ArrowCircleLeft';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FlagIcon from '@mui/icons-material/Flag';
import SchoolIcon from '@mui/icons-material/School';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Modal from '@mui/material/Modal';
import LoginComponent from '../Login';
import { ModalStyle } from '../Util';
import PaidIcon from '@mui/icons-material/Paid';
import ReactLoading from 'react-loading';
import * as CustomUtil from '../CustomUtil';
import { selectPackages } from '../../store/slices/packagesSlice';
import { selectUser } from '../../store/slices/userSlice';
import { selectAutoCollapseAnswers } from '../../store/slices/settingsSlice';
import { saveQuestion, deleteSavedQuestion } from '../../store/slices/savedQuestionsSlice';
import apiService from '../../services/api';
import packageCache from '../../utils/packageCache';
import QuestionReportModal from './QuestionReportModal';
import './singleView.scss';
import PdfDownloadModal from "./PdfDownloadModal";

const TIMER_START = 60;

const PackageSingleView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const packages = useSelector(selectPackages);
    const user = useSelector(selectUser);
    const autoCollapseAnswers = useSelector(selectAutoCollapseAnswers);

    const [packageDto, setPackageDto] = useState({});
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [apiErrorCode, setApiErrorCode] = useState(null);
    const [showPdfDownloadModal, setShowPdfDownloadModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [securityError, setSecurityError] = useState(false);

    // Answer expand overrides: { [questionId]: true/false }
    const [answerOverrides, setAnswerOverrides] = useState({});

    // Per-card timer state (single active timer across all cards)
    const [activeTimerId, setActiveTimerId] = useState(null);
    const [timerSeconds, setTimerSeconds] = useState(TIMER_START);
    const timerIntervalRef = useRef(null);

    // Question report modal
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [reportQuestionId, setReportQuestionId] = useState(null);

    const isFetchingRef = useRef(false);
    const hasFetchedRef = useRef(false);

    // ── Data fetching ────────────────────────────────────────────────────────

    const fetchPackageData = useCallback(async (packageId) => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        hasFetchedRef.current = true;

        const cachedData = packageCache.get(packageId);
        if (cachedData) {
            setPackageDto(cachedData);
            setQuestions(cachedData.questions);
            setLoading(false);
            isFetchingRef.current = false;
            return;
        }

        setLoading(true);
        setSecurityError(false);
        setApiErrorCode(null);

        try {
            const json = await apiService.packages.getById(packageId);
            packageCache.set(packageId, json);
            setPackageDto(json);
            setQuestions(json.questions);
            setLoading(false);
            setError(null);
        } catch (error) {
            setLoading(false);
            if (error.errorCode === 'RATE_LIMIT_EXCEEDED' || error.errorCode === 'ACCOUNT_BLOCKED') {
                setApiErrorCode(error.errorCode);
                setError(error.message);
            } else if (error.status === 403) {
                setSecurityError(true);
                setError('Xavfsizlik xatosi. Iltimos, ilovani yangilang.');
            } else {
                setError(error.message);
            }
        } finally {
            isFetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (hasFetchedRef.current) return;
        const packageId = new URLSearchParams(window.location.search).get('id');
        fetchPackageData(packageId);

        setTimeout(() => {
            try { navigator.wakeLock.request('screen'); } catch {}
        }, 3000);
    }, [fetchPackageData]);

    useEffect(() => {
        return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
    }, []);

    // ── Per-card timer ───────────────────────────────────────────────────────

    const startCardTimer = (questionId) => {
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        setActiveTimerId(questionId);
        setTimerSeconds(TIMER_START);

        timerIntervalRef.current = setInterval(() => {
            setTimerSeconds(prev => {
                if (prev <= 1) {
                    clearInterval(timerIntervalRef.current);
                    playDing();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const resetCardTimer = () => {
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        setActiveTimerId(null);
        setTimerSeconds(TIMER_START);
    };

    const playDing = () => {
        try {
            new Audio(`${process.env.PUBLIC_URL}/ding-36029.mp3`).play().catch(() => {});
        } catch {}
    };

    const timerColor = timerSeconds > 40 ? 'green' : timerSeconds > 20 ? 'orange' : 'red';

    // ── Answer expand helpers ────────────────────────────────────────────────

    const isAnswerExpanded = (questionId) =>
        questionId in answerOverrides ? answerOverrides[questionId] : autoCollapseAnswers;

    const handleAnswerToggle = (questionId, expanded) =>
        setAnswerOverrides(prev => ({ ...prev, [questionId]: expanded }));

    // ── Save / delete ────────────────────────────────────────────────────────

    const handleSaveQuestion = async (questionId) => {
        if (!user?.email) { setShowLoginModal(true); return; }
        const packageId = new URLSearchParams(window.location.search).get('id');
        try {
            await dispatch(saveQuestion({ questionId, packageId })).unwrap();
            setQuestions(qs => qs.map(q => q.id === questionId ? { ...q, saved: true } : q));
        } catch (error) {
            if (error.includes?.('signature') || error.includes?.('403')) setSecurityError(true);
        }
    };

    const handleDeleteSavedQuestion = async (questionId) => {
        const packageId = new URLSearchParams(window.location.search).get('id');
        try {
            await dispatch(deleteSavedQuestion({ questionId, packageId })).unwrap();
            setQuestions(qs => qs.map(q => q.id === questionId ? { ...q, saved: false } : q));
        } catch (error) {
            if (error.includes?.('signature') || error.includes?.('403')) setSecurityError(true);
        }
    };

    const handleRetry = () => {
        const packageId = new URLSearchParams(window.location.search).get('id');
        setSecurityError(false);
        setApiErrorCode(null);
        setError(null);
        hasFetchedRef.current = false;
        fetchPackageData(packageId);
    };

    // ── PDF modal ────────────────────────────────────────────────────────────

    const getPdfDownloadModalContent = () => {
        if (!user?.email) {
            return (
                <LoginComponent
                    text={'To\'plamni PDF formatdan yuklab olish uchun quyidagi veb-servislardan foydalangan holda avtorizatsiyadan oʼtishingiz lozim:'}
                />
            );
        }
        if (!packageDto.canDownload) {
            return (
                <div className={'cannot-download'}>
                    <Box sx={ModalStyle}>
                        <Typography sx={{ mt: 2 }}><PaidIcon /></Typography>
                        <Typography sx={{ mt: 2 }}><span>PDF saqlash tarifi aktivlashmagan!</span></Typography>
                        <Typography sx={{ mt: 2 }} style={{color: '#a2a2a2'}}><span>(PDF saqlash tarifini sotib oling)</span></Typography>
                    </Box>
                </div>
            );
        }
        const compact = apiService.packages.getPdfDownloadUrl(packageDto.id, false);
        const separate = apiService.packages.getPdfDownloadUrl(packageDto.id, true);
        return (
            <div className={'pdf-download'}>
                <Box sx={ModalStyle}>
                    <Typography id='pdf-title' variant='h6' component='h2' style={{textAlign: 'center'}}>PDF qilib saqlash</Typography>
                    <Typography id='pdf-download' sx={{ mt: 2 }}>
                        <div className='download-option'>
                            <a href={compact} target={'_blank'} rel='noreferrer' className='compact'>
                                <span>Savol va javobi bir sahifada</span>
                            </a>
                        </div>
                        <div className='download-option'>
                            <a href={separate} target={'_blank'} rel='noreferrer' className='separate'>
                                <span>Javoblari alohida sahifada</span>
                            </a>
                        </div>
                    </Typography>
                </Box>
            </div>
        );
    };

    // ── Loading / error states ───────────────────────────────────────────────

    if (loading) {
        return (
            <div className={'package-loading'}>
                <ReactLoading type={'spin'} color={'#03989F'} height={'20%'} width={'20%'} />
                <p>To'plam yuklanmoqda...</p>
            </div>
        );
    }

    if (error) {
        const isApiError = apiErrorCode === 'RATE_LIMIT_EXCEEDED' || apiErrorCode === 'ACCOUNT_BLOCKED';
        return (
            <div className={'package-error'}>
                <h2>Xatolik yuz berdi</h2>
                {isApiError ? (
                    <div className="api-error-notice">
                        <p>{error}</p>
                        <a
                            href="https://t.me/zakovat_savollari_com"
                            target="_blank"
                            rel="noreferrer"
                            className="telegram-contact-btn"
                        >
                            Telegram orqali bog'lanish
                        </a>
                    </div>
                ) : (
                    <p>{error}</p>
                )}
                {securityError && (
                    <div className="security-notice">
                        <p>Xavfsizlik sozlamalari o'zgardi. Ilovani yangilashingiz kerak.</p>
                        <button onClick={() => window.location.reload()}>Sahifani Yangilash</button>
                    </div>
                )}
                <button onClick={handleRetry} disabled={securityError || isApiError}>Qayta Urinish</button>
                <button onClick={() => navigate('/')}>Orqaga Qaytish</button>
            </div>
        );
    }

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className={'package-single-view fade-in'}>
            <div className={'header'}>
                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2} columns={16}>
                        <Grid size={3}>
                            <ArrowCircleLeft
                                onClick={() => navigate('/')}
                                style={{cursor: 'pointer', padding: 0, marginTop: '4px'}}
                            />
                        </Grid>
                        <Grid size={10}>
                            <div className={'title'}>{packageDto.name}</div>
                            <div className={'middle-text'}>
                                <span>{packageDto.totalQuestion} savol</span>
                                <span>{packageDto.numberOfPictureQuestions} rasmli</span>
                            </div>
                        </Grid>
                        <Grid size={3}>
                            <div className={'pdf'} onClick={() => setShowPdfDownloadModal(true)}>
                                <DownloadForOfflineIcon/>
                                <div>PDF</div>
                            </div>
                        </Grid>
                    </Grid>
                </Box>
                <div
                    className={'practice-mode-btn'}
                    onClick={() => navigate(`/practice/${packageDto.id}`)}
                >
                    <SchoolIcon fontSize="small" />
                    <span>Mashq rejimi</span>
                </div>
            </div>

            {securityError && (
                <div className="security-warning">
                    <p>⚠️ Xavfsizlik sozlamalari yangilandi. Ba'zi funksiyalar ishlamasligi mumkin.</p>
                </div>
            )}

            {questions && questions.map((question, index) => (
                <div
                    className={'question-card'}
                    key={question.positionInPackage}
                    style={{animationDelay: `${index * 0.05}s`}}
                >
                    <Accordion defaultExpanded={index === 0}>
                        <AccordionSummary
                            aria-controls='panel1-content'
                            id={'panel-header-' + question.positionInPackage}
                            className={'panel-header'}
                            expandIcon={<ExpandMoreIcon className={'expand-icon'}/>}
                        >
                            <Typography>Savol - {question.positionInPackage + 1}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography className={'body'}>
                                {CustomUtil.printImage(question)}
                                <div dangerouslySetInnerHTML={{ __html: question.questionText }} />
                                <br/>
                                <div dangerouslySetInnerHTML={{ __html: question.question }} />

                                <div className={'question-actions'}>
                                    {/* Per-card timer */}
                                    <div className={'card-timer-chip'}>
                                        {activeTimerId !== question.id ? (
                                            <PlayCircleIcon
                                                className="card-timer-play"
                                                onClick={() => startCardTimer(question.id)}
                                            />
                                        ) : timerSeconds > 0 ? (
                                            <span className={`card-timer-seconds ${timerColor}`}>
                                                {timerSeconds}s
                                            </span>
                                        ) : (
                                            <>
                                                <span className="card-timer-seconds red">0s</span>
                                                <ReplayIcon
                                                    className="card-timer-reset"
                                                    onClick={resetCardTimer}
                                                />
                                            </>
                                        )}
                                    </div>

                                    {/* Action icons */}
                                    <div className={'action-icons'}>
                                        <FlagIcon
                                            className="flag-icon"
                                            onClick={() => {
                                                setReportQuestionId(question.id);
                                                setReportModalOpen(true);
                                            }}
                                        />
                                        {!question.saved
                                            ? <FavoriteBorderIcon onClick={() => handleSaveQuestion(question.id)} />
                                            : <FavoriteIcon onClick={() => handleDeleteSavedQuestion(question.id)} />
                                        }
                                    </div>
                                </div>
                            </Typography>

                            <Accordion
                                expanded={isAnswerExpanded(question.id)}
                                onChange={(_, expanded) => handleAnswerToggle(question.id, expanded)}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon className={'expand-icon'}/>}
                                    aria-controls='panel2-content'
                                    className={'panel-header'}
                                    id={'panel-header-answer-' + question.positionInPackage}
                                >
                                    <Typography>Javobni ko'rish</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography>
                                        {CustomUtil.printAnswerImage(question)}
                                        <div dangerouslySetInnerHTML={{ __html: question.answer }} />
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        </AccordionDetails>
                    </Accordion>
                </div>
            ))}

            <div className={'bottom-space'}>SPACE</div>

            <PdfDownloadModal
                open={showPdfDownloadModal}
                onClose={() => setShowPdfDownloadModal(false)}
                packageId={packageDto.id}
                canDownload={packageDto.canDownload}
                user={user}
            />

            <Modal
                open={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                aria-labelledby='login-modal-title'
                aria-describedby='login-modal-description'
            >
                <LoginComponent text={'Savolni saqlash uchun sahifamizda hisob ochishingiz lozim.'} />
            </Modal>

            <QuestionReportModal
                open={reportModalOpen}
                onClose={() => { setReportModalOpen(false); setReportQuestionId(null); }}
                questionId={reportQuestionId}
                isLoggedIn={!!user?.email}
            />

            <MobileNavigation/>
        </div>
    );
};

export default PackageSingleView;